import { Component, OnInit, ViewChild, EventEmitter, Output, Input } from '@angular/core';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Form } from '@atefhashem/eforms';
import { FormTemplate } from 'app/models/template';
import { FormSubmission } from 'app/models/form-submission';
import { basename } from 'path';
import { Validators, FormGroup, FormBuilder } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { loopOnComponents } from 'app/helpers/utils';
import { FormTemplateService } from 'app/services/form-template.service';
import { ConnectedAppAuth } from 'app/models/connected-app';
import { finalize } from 'rxjs/operators';
import { ConnectedAppAuthorizeComponent } from '../connected-apps/connected-app-authorize/connected-app-authorize.component';
import { DataService } from 'app/services/data.service';
import { UserService } from 'app/services/user.service';
import { TeamService } from 'app/services/team.service';
import { ContactService } from 'app/services/contactService';

@Component({
  selector: 'app-form-filler',
  templateUrl: './form-filler.component.html',
  styleUrls: ['./form-filler.component.scss']
})
export class FormFillerComponent implements OnInit {

  @ViewChild('fillerModal') fillerModal: ModalDirective;
  @ViewChild('submissionNameModal') submissionNameModal: ModalDirective;
  @ViewChild('authorizeApp') authorizeApp: ConnectedAppAuthorizeComponent;

  @Output() fill: EventEmitter<{form: FormTemplate, submission: FormSubmission}> = new EventEmitter<{form: FormTemplate, submission: FormSubmission}>();
  @Input() requireSubmissionName: boolean = true;

  public title: string = null;

  currentForm: FormTemplate = null;
  currentSubmission: FormSubmission = null;

  submissionNameFormGroup: FormGroup;
  
  uniqueID: string = '';

  isCheckingAuth: boolean = false;

  constructor(private validationHandler: ValidationHandler, private formBuilder: FormBuilder, private formTemplateService: FormTemplateService,
              private dataService: DataService, private userService: UserService, private teamService: TeamService, private contactService: ContactService) {
    this.submissionNameFormGroup = this.formBuilder.group({
      name: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.uniqueID =  Math.random().toString(36).substr(2, 9);
    this.currentForm = null;
    this.currentSubmission = null;
  }

  public onModalClose() {   
    // clean up
    document.getElementById('eform_filler_' + this.uniqueID).innerHTML = "";
  }

  onSubmissionName() {
    this.validationHandler.onSubmitCheck(this.submissionNameFormGroup);
    if (!this.submissionNameFormGroup.valid) {
      return;
    }
    this.submissionNameModal.hide();
    this.currentSubmission.name = this.submissionNameFormGroup.get('name').value;
    this.submissionNameFormGroup.reset();
    
    this.fill.emit({form: this.currentForm, submission: this.currentSubmission});
    this.fillerModal.hide();
  }

  public show(form: FormTemplate, submission: FormSubmission = null, title: string = null, requiredFields: string[] = null, disableOtherFields = false, htmlMode = false, removeSubmitButton = false, optionalFields: string[] = null) {

    this.currentForm = form;
    if (submission) {
      this.currentSubmission = JSON.parse(JSON.stringify(submission));
    } else {
      this.currentSubmission = {template_id: form.id} as FormSubmission;
    }

    // fastCloneDeep
    let schema: any = JSON.parse(JSON.stringify(form.schema));

    if (title == null) {
      if (submission) {
        this.title = submission.name;
      } else {
        this.title = basename(form.path);
      }
    } else {
      this.title = title;
    }

    var that = this;
    // Override required
    if (requiredFields !== null) {
      loopOnComponents(schema, (component, submissionPrefix) => {
        if (requiredFields.includes(submissionPrefix + component.key)) {
          component.validate = (component.validate)? component.validate: {};
          component.validate.required = true;
        } else {
          component.validate = (component.validate)? component.validate: {};
          component.validate.required = false;
          if (disableOtherFields && (!(optionalFields || []).includes(submissionPrefix + component.key)) && component.key != 'submit') {
            component.disabled = true;
          }
        }
      });
    }
    
    // Remove Submit button
    if (removeSubmitButton) {
      loopOnComponents(schema, component => {
        if (component.key == 'submit' || (component.type == 'button' && component.action == 'submit')) {
          component.hidden = true;
        }
      });
    }

    if (htmlMode) {
      loopOnComponents(schema, component => {
        component.calculateValue = undefined;
        component.dataSrc = undefined;
      });
    }

    var formioForm = new Form(document.getElementById('eform_filler_' + this.uniqueID), schema, {
      renderMode: htmlMode? 'html': 'form',
      noAlerts: true,
      submissionTimezone: submission && submission.timezone,
      evalContext: htmlMode? {} : {
        getFormQueryExternalOperationObservable: (uniqueName: string, options: any) => {
          return this.formTemplateService.executeFormQueryExternalOperation(form.id, uniqueName, options, this.currentForm.external_access_token);
        },
        getDataFieldTermsObservable: (indexPattern: string, fieldKey: string, searchFilter: string = '') =>  {
          return this.dataService.getFieldTerms(indexPattern, fieldKey, searchFilter, this.currentForm.external_access_token);
        },
        getDataRawSearchObservable: (indexPattern: string, body: any) => {
          return this.dataService.rawSearch(indexPattern, body, this.currentForm.external_access_token);
        },
        getDataSQLQueryObservable: (query: string, format: string = 'json') => {
          return this.dataService.sqlQueryResults(query, format, this.currentForm.external_access_token);
        },
        /** @note these do not work in external access yet */
        getUsersObservable: () => {
          return this.userService.getAll();
        },
        getTeamsObservable: () => {
          return this.teamService.getAll();
        },
        getContactsObservable: () => {
          return this.contactService.getAll();
        }
      }
    });
    /** @todo this doesn't get fired for PDF forms for some reason */
    formioForm.ready.then((f) => {
      if (submission) {
        f.submission = { data: submission.value };
      }
      
      // listen for submit
      f.on('submit', (s) => {
        that.currentSubmission.value = s.data;
        if (that.requireSubmissionName) {
          this.submissionNameFormGroup.get('name').setValue(that.currentSubmission.name || '');
          that.submissionNameModal.show();
        } else {
          that.fill.emit({form: that.currentForm, submission: that.currentSubmission});
          that.fillerModal.hide();
        }
      });
    });

    
    formioForm.render();    

    let requiredUserAuths: ConnectedAppAuth[] = form.required_user_auths;
    if (requiredUserAuths && requiredUserAuths.length == 0) {
      this.showForm();
    } else if (requiredUserAuths) {
      this.isCheckingAuth = true;
      this.authorizeApp.showIfNeedsAuthorization(requiredUserAuths, false).pipe(finalize(() => this.isCheckingAuth = false)).subscribe((isAllAuthorized) => {
        if (isAllAuthorized) {
          this.showForm();
        }
      });
    } else {
      this.showForm();
    }  
  }

  showForm() {
    this.fillerModal.show();
  }
}
